import { Component } from '@theme/component';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

const variantCache = new Map();
const productCache = new Map();
const STOREFRONT_API_VERSION = '2026-04';

function normalizeVariantId(rawId) {
  const value = String(rawId ?? '').trim();
  if (!value) return '';

  const match = value.match(/(\d+)(?:\D*)$/);
  return match ? match[1] : value;
}

function normalizeStoreUrl(rawUrl) {
  const value = String(rawUrl ?? '').trim();
  if (!value) return '';

  if (value.startsWith('http://') || value.startsWith('https://')) {
    return value.replace(/\/+$/, '');
  }

  return `https://${value.replace(/^\/+|\/+$/g, '')}`;
}

function normalizeProductHandle(rawHandle) {
  const value = String(rawHandle ?? '').trim();
  if (!value) return '';

  return value.replace(/^https?:\/\/[^/]+/i, '').replace(/^\/+/, '').replace(/^products\//, '').replace(/\/+$/, '');
}

function getStoreContainer(element) {
  return element.closest('[data-storefront-url][data-storefront-token]');
}

function getStoreConfig(element) {
  const container = getStoreContainer(element);
  const storefrontUrl = normalizeStoreUrl(element.dataset.storefrontUrl || container?.dataset.storefrontUrl);
  const storefrontToken = String(element.dataset.storefrontToken || container?.dataset.storefrontToken || '').trim();

  if (!storefrontUrl || !storefrontToken) return null;

  return {
    endpoint: `${storefrontUrl}/api/${STOREFRONT_API_VERSION}/graphql.json`,
    token: storefrontToken,
  };
}

function getMoneyFormat(element) {
  const container = getStoreContainer(element);
  return element.dataset.moneyFormat || container?.dataset.moneyFormat || '{{amount}}';
}

async function fetchVariant(element, rawVariantId) {
  const variantId = normalizeVariantId(rawVariantId);
  if (!variantId) return null;
  const storeConfig = getStoreConfig(element);
  if (!storeConfig) return null;

  const cacheKey = `${storeConfig.endpoint}::${variantId}`;

  if (!variantCache.has(cacheKey)) {
    variantCache.set(
      cacheKey,
      fetch(storeConfig.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storeConfig.token,
        },
        body: JSON.stringify({
          query: `query VariantById($id: ID!) {
            node(id: $id) {
              __typename
              ... on ProductVariant {
                id
                price {
                  amount
                  currencyCode
                }
              }
            }
          }`,
          variables: {
            id: `gid://shopify/ProductVariant/${variantId}`,
          },
        }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to load variant ${variantId} from Storefront API`);
          }

          return response.json();
        })
        .then((payload) => {
          if (payload.errors?.length) {
            throw new Error(payload.errors.map((error) => error.message).join(', '));
          }

          return payload.data?.node?.__typename === 'ProductVariant' ? payload.data.node : null;
        })
        .catch((error) => {
          console.error(error);
          variantCache.delete(cacheKey);
          return null;
        })
    );
  }

  return variantCache.get(cacheKey);
}

async function fetchProductByHandle(element, rawHandle) {
  const handle = normalizeProductHandle(rawHandle);
  if (!handle) return null;
  const storeConfig = getStoreConfig(element);
  if (!storeConfig) return null;

  const cacheKey = `${storeConfig.endpoint}::product::${handle}`;

  if (!productCache.has(cacheKey)) {
    productCache.set(
      cacheKey,
      fetch(storeConfig.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storeConfig.token,
        },
        body: JSON.stringify({
          query: `query ProductByHandle($handle: String!) {
            product(handle: $handle) {
              handle
              selectedOrFirstAvailableVariant {
                id
                price {
                  amount
                  currencyCode
                }
              }
            }
          }`,
          variables: {
            handle,
          },
        }),
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Failed to load product ${handle} from Storefront API`);
          }

          return response.json();
        })
        .then((payload) => {
          if (payload.errors?.length) {
            throw new Error(payload.errors.map((error) => error.message).join(', '));
          }

          return payload.data?.product?.selectedOrFirstAvailableVariant ?? null;
        })
        .catch((error) => {
          console.error(error);
          productCache.delete(cacheKey);
          return null;
        })
    );
  }

  return productCache.get(cacheKey);
}

function getFormattedPrice(variant, moneyFormat, currency) {
  const amount = variant?.price?.amount;
  const currencyCode = variant?.price?.currencyCode || currency || 'EUR';
  if (!amount) return '';

  const moneyValue = convertMoneyToMinorUnits(String(amount), currencyCode);
  if (moneyValue == null) return '';

  return formatMoney(moneyValue, moneyFormat || '{{amount}}', currencyCode);
}

function applyTemplate(template, price) {
  if (!template || !price) return '';

  return template.replace(/%price%/gi, price);
}

class PriceFetchElement extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.setLoading(true);
  }

  setLoading(isLoading) {
    this.toggleAttribute('data-loading', isLoading);

    if (isLoading) {
      this.hidden = false;
      this.setAttribute('aria-busy', 'true');
    } else {
      this.removeAttribute('aria-busy');
    }
  }
}

class AsPrice extends PriceFetchElement {
  async connectedCallback() {
    super.connectedCallback();
    await this.renderPrice();
  }

  async renderPrice() {
    const variant = await fetchVariant(this, this.dataset.variantId);
    const price = getFormattedPrice(variant, getMoneyFormat(this), this.dataset.currency);

    this.setLoading(false);
    this.textContent = price;
    this.hidden = !price;
  }
}

class AsQuoteDisclaimer extends PriceFetchElement {
  async connectedCallback() {
    super.connectedCallback();
    await this.renderDisclaimer();
  }

  async renderDisclaimer() {
    const variant = await fetchVariant(this, this.dataset.variantId);
    const price = getFormattedPrice(variant, getMoneyFormat(this), this.dataset.currency);
    const template = this.dataset.template ?? "oppure acconto %price% + saldo prima dell'inizio";

    this.setLoading(false);
    this.textContent = applyTemplate(template, price);
    this.hidden = !price;
  }
}

class AsStorefrontPrice extends PriceFetchElement {
  async connectedCallback() {
    super.connectedCallback();
    await this.renderPrice();
  }

  async renderPrice() {
    const handleSource =
      this.dataset.productHandle || this.closest('[data-external-product-handle]')?.dataset.externalProductHandle;
    const variant = await fetchProductByHandle(this, handleSource);
    const price = getFormattedPrice(variant, getMoneyFormat(this), this.dataset.currency);

    this.setLoading(false);
    this.textContent = price;
    this.hidden = !price;
  }
}

if (!customElements.get('as-price')) {
  customElements.define('as-price', AsPrice);
}

if (!customElements.get('as-quote-disclaimer')) {
  customElements.define('as-quote-disclaimer', AsQuoteDisclaimer);
}

if (!customElements.get('as-storefront-price')) {
  customElements.define('as-storefront-price', AsStorefrontPrice);
}
